import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "errorPlan",
    "errorPlanAmountTeamLabel",
    "errorPlanNoticesTeamLabel",
    "errorPlanPurgeDaysTeamLabel",
    "errorPlanAmountBusinessLabel",
    "errorPlanNoticesBusinessLabel",
    "errorPlanPurgeDaysBusinessLabel",
    "insightsPlan",
    "insightsPlanAmountTeamLabel",
    "insightsPlanQuotaTeamLabel",
    "insightsPlanPurgeDaysTeamLabel",
    "insightsPlanAmountBusinessLabel",
    "insightsPlanQuotaBusinessLabel",
    "insightsPlanPurgeDaysBusinessLabel",
    "monitorCount",
    "monitoringPlanAmountTeamLabel",
    "monitoringPlanAmountBusinessLabel",
    "statusPagesCount",
    "statusPagesPlanAmountTeamLabel",
    "statusPagesPlanAmountBusinessLabel",
    "basePlanAmountTeamLabel",
    "basePlanAmountBusinessLabel",
    "totalAmountTeamLabel",
    "totalAmountBusinessLabel",
    "teamSubscriptionPlanId",
    "businessSubscriptionPlanId",
    "teamErrorSubscriptionPlanId",
    "businessErrorSubscriptionPlanId",
    "teamInsightsSubscriptionPlanId",
    "businessInsightsSubscriptionPlanId",
    "teamMonitoringSubscriptionPlanId",
    "businessMonitoringSubscriptionPlanId",
    "teamStatusPagesSubscriptionPlanId",
    "businessStatusPagesSubscriptionPlanId",
    "teamPlanSubmit",
    "currentTeamPlanMessage",
    "businessPlanSubmit",
    "currentBusinessPlanMessage",
    "monthButton",
    "yearButton",
    "intervalLabel",
  ];

  static values = {
    currentPlanIds: Array,
    includedMonitorCount: Number,
    includedStatusPagesCount: Number,
    basePlanData: Object,
    monitoringPlanData: Object,
    statusPagesPlanData: Object,
  };

  connect() {
    this.selectedInterval = "month";
    this.baseTeamAmount = 0;
    this.baseBusinessAmount = 0;
    this.monitoringTeamAmount = 0;
    this.monitoringBusinessAmount = 0;
    this.statusPagesTeamAmount = 0;
    this.statusPagesBusinessAmount = 0;
    this.errorTeamAmount = 0;
    this.errorBusinessAmount = 0;
    this.insightsTeamAmount = 0;
    this.insightsBusinessAmount = 0;

    this.updatePricing();
  }

  updatePricing() {
    this.intervalUpdate();
    this.basePlanUpdate();
    this.errorPlanUpdate();
    this.insightsPlanUpdate();
    this.monitoringPlanUpdate();
    this.statusPagesPlanUpdate();
    this.totalAmountUpdate();
  }

  intervalUpdate() {
    if (this.monthButtonTarget.checked) {
      this.selectedInterval = "month";
    } else {
      this.selectedInterval = "year";
    }
    if (this.hasIntervalLabelTarget) {
      this.intervalLabelTargets.forEach(
        (target) => (target.innerHTML = this.selectedInterval),
      );
    }
  }

  basePlanUpdate() {
    this.baseTeamPlan = this.basePlanDataValue["team"][this.selectedInterval];
    this.baseBusinessPlan =
      this.basePlanDataValue["business"][this.selectedInterval];

    this.baseTeamAmount = parseFloat(this.baseTeamPlan.amount);
    this.baseBusinessAmount = parseFloat(this.baseBusinessPlan.amount);

    this.basePlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.baseTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.basePlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.baseBusinessAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );

    this.teamSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.baseTeamPlan.id),
    );
    this.businessSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.baseBusinessPlan.id),
    );
  }

  monitoringPlanUpdate() {
    this.monitoringTeamPlan =
      this.monitoringPlanDataValue["team"][this.selectedInterval];
    this.monitoringBusinessPlan =
      this.monitoringPlanDataValue["business"][this.selectedInterval];

    this.monitoringTeamAmount =
      parseFloat(this.monitoringTeamPlan.amount) * this.extraMonitorCount();
    this.monitoringBusinessAmount =
      parseFloat(this.monitoringBusinessPlan.amount) * this.extraMonitorCount();

    this.monitoringPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.monitoringTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.monitoringPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.monitoringBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );

    this.teamMonitoringSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.monitoringTeamPlan.id),
    );
    this.businessMonitoringSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.monitoringBusinessPlan.id),
    );
  }

  statusPagesPlanUpdate() {
    this.statusPagesTeamPlan =
      this.statusPagesPlanDataValue["team"][this.selectedInterval];
    this.statusPagesBusinessPlan =
      this.statusPagesPlanDataValue["business"][this.selectedInterval];

    this.statusPagesTeamAmount =
      parseFloat(this.statusPagesTeamPlan.amount) * this.extraStatusPageCount();
    this.statusPagesBusinessAmount =
      parseFloat(this.statusPagesBusinessPlan.amount) *
      this.extraStatusPageCount();

    this.statusPagesPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.statusPagesTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.statusPagesPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.statusPagesBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );

    this.teamStatusPagesSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.statusPagesTeamPlan.id),
    );
    this.businessStatusPagesSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.statusPagesBusinessPlan.id),
    );
  }

  errorPlanUpdate() {
    let selectedOption =
      this.errorPlanTarget.options[this.errorPlanTarget.selectedIndex];
    this.errorTeamPlan = JSON.parse(selectedOption.dataset.planData)["team"][
      this.selectedInterval
    ];
    this.errorBusinessPlan = JSON.parse(selectedOption.dataset.planData)[
      "business"
    ][this.selectedInterval];

    this.errorTeamAmount = parseFloat(this.errorTeamPlan.amount);
    this.errorBusinessAmount = parseFloat(this.errorBusinessPlan.amount);

    this.errorPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.errorTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.errorPlanNoticesTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.errorTeamAmount > 0 ? "" : " included"}`),
    );
    this.errorPlanPurgeDaysTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.errorTeamPlan.purge_days} days retention`),
    );

    this.errorPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.errorBusinessAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.errorPlanNoticesBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.errorBusinessAmount > 0 ? "" : " included"}`),
    );
    this.errorPlanPurgeDaysBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.errorBusinessPlan.purge_days} days retention`),
    );

    this.teamErrorSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.errorTeamPlan.id),
    );
    this.businessErrorSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.errorBusinessPlan.id),
    );
  }

  insightsPlanUpdate() {
    let selectedOption =
      this.insightsPlanTarget.options[this.insightsPlanTarget.selectedIndex];
    this.insightsTeamPlan = JSON.parse(selectedOption.dataset.planData)["team"][
      this.selectedInterval
    ];
    this.insightsBusinessPlan = JSON.parse(selectedOption.dataset.planData)[
      "business"
    ][this.selectedInterval];

    this.insightsTeamAmount = parseFloat(this.insightsTeamPlan.amount);
    this.insightsBusinessAmount = parseFloat(this.insightsBusinessPlan.amount);

    this.insightsPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.insightsTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.insightsPlanQuotaTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.insightsTeamAmount > 0 ? "" : " included"}`),
    );
    this.insightsPlanPurgeDaysTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.insightsTeamPlan.purge_days} days retention`),
    );

    this.insightsPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.insightsBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );
    this.insightsPlanQuotaBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.insightsBusinessAmount > 0 ? "" : " included"}`),
    );
    this.insightsPlanPurgeDaysBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.insightsBusinessPlan.purge_days} days retention`),
    );

    this.teamInsightsSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.insightsTeamPlan.id),
    );
    this.businessInsightsSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.insightsBusinessPlan.id),
    );
  }

  totalAmountUpdate() {
    this.totalTeamAmount =
      this.baseTeamAmount +
      this.errorTeamAmount +
      this.insightsTeamAmount +
      this.monitoringTeamAmount +
      this.statusPagesTeamAmount;
    this.totalBusinessAmount =
      this.baseBusinessAmount +
      this.errorBusinessAmount +
      this.insightsBusinessAmount +
      this.monitoringBusinessAmount +
      this.statusPagesBusinessAmount;

    this.totalAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.totalTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.totalAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.totalBusinessAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );

    if (
      JSON.stringify(this.selectedTeamPlanIds().sort()) !==
      JSON.stringify(this.currentPlanIdsValue.sort())
    ) {
      this.teamPlanSubmitTarget.classList.remove("hidden");
      this.currentTeamPlanMessageTarget.classList.add("hidden");
    } else {
      this.teamPlanSubmitTarget.classList.add("hidden");
      this.currentTeamPlanMessageTarget.classList.remove("hidden");
    }

    if (
      JSON.stringify(this.selectedBusinessPlanIds().sort()) !==
      JSON.stringify(this.currentPlanIdsValue.sort())
    ) {
      this.businessPlanSubmitTarget.classList.remove("hidden");
      this.currentBusinessPlanMessageTarget.classList.add("hidden");
    } else {
      this.businessPlanSubmitTarget.classList.add("hidden");
      this.currentBusinessPlanMessageTarget.classList.remove("hidden");
    }
  }

  selectedTeamPlanIds() {
    return [
      this.baseTeamPlan.id,
      this.errorTeamPlan.id,
      this.insightsTeamPlan.id,
      this.monitoringTeamPlan.id,
      this.statusPagesTeamPlan.id,
    ];
  }

  selectedBusinessPlanIds() {
    return [
      this.baseBusinessPlan.id,
      this.errorBusinessPlan.id,
      this.insightsBusinessPlan.id,
      this.monitoringBusinessPlan.id,
      this.statusPagesBusinessPlan.id,
    ];
  }

  extraMonitorCount() {
    let monitorCountValue = parseInt(this.monitorCountTarget.value);
    if (monitorCountValue > this.includedMonitorCountValue) {
      return monitorCountValue - this.includedMonitorCountValue;
    } else {
      return 0;
    }
  }

  extraStatusPageCount() {
    let statusPagesCountValue = parseInt(this.statusPagesCountTarget.value);
    if (statusPagesCountValue > this.includedStatusPagesCountValue) {
      return statusPagesCountValue - this.includedStatusPagesCountValue;
    } else {
      return 0;
    }
  }
}
